import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { apiService } from '../services/api';
import {
  fetchOrderById,
  updateOrderStatus,
  updateOrderPayment,
  clearCurrentOrder,
  generateOrderInvoice,
  fetchOrderInvoice,
} from '../store/slices/ordersSlice';
import { downloadInvoicePdf } from '../utils/invoicePdfDownload';
import StatusChangeModal from '../components/StatusChangeModal';
import './OrderDetail.css';

// Maps the modal's capitalized status values to the backend's lowercase enum values
const STATUS_MAP = {
  Pending: 'pending',
  Processing: 'processing',
  Completed: 'delivered',
  Cancelled: 'cancelled',
  Confirmed: 'confirmed',
  Shipped: 'shipped',
};

const PAYMENT_MAP = {
  Pending: 'pending',
  Paid: 'completed',
  Refunded: 'refunded',
};

// Maps backend status → display label
const STATUS_DISPLAY = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Completed',
  cancelled: 'Cancelled',
};

const PAYMENT_DISPLAY = {
  pending: 'Pending',
  completed: 'Paid',
  failed: 'Failed',
  refunded: 'Refunded',
};

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentOrder: order, currentOrderInvoice, detailLoading, invoiceLoading, error } = useSelector((state) => state.orders);
  const businessAccountId = useSelector((state) => state.auth.businessAccountId);

  const [isExporting, setIsExporting] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPayment, setSelectedPayment] = useState('');


  // Sync dropdown selections when order loads
  useEffect(() => {
    if (order) {
      setSelectedStatus(order.status || '');
      setSelectedPayment(order.paymentStatus || '');
    }
  }, [order]);

  const handleUpdateOrderStatus = async () => {
    if (!order || !selectedStatus || selectedStatus === order.status) return;
    setIsUpdatingStatus(true);
    try {
      await dispatch(updateOrderStatus({
        id: order._id,
        status: selectedStatus,
        businessAccountId,
      })).unwrap();
      toast.success('Order status updated successfully');
    } catch (err) {
      toast.error(err?.message || err?.error || 'Failed to update order status');
      setSelectedStatus(order.status || '');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleUpdatePaymentStatus = async () => {
    if (!order || !selectedPayment || selectedPayment === order.paymentStatus) return;
    setIsUpdatingPayment(true);
    try {
      await dispatch(updateOrderPayment({
        id: order._id,
        paymentStatus: selectedPayment,
        businessAccountId,
      })).unwrap();
      toast.success('Payment status updated successfully');
    } catch (err) {
      toast.error(err?.message || err?.error || 'Failed to update payment status');
      setSelectedPayment(order.paymentStatus || '');
    } finally {
      setIsUpdatingPayment(false);
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchOrderById({ id }))
        .unwrap()
        .then((result) => {
          // After loading the order, try to fetch its linked invoice
          const loaded = result?.data || result;
          if (loaded?.invoiceId) {
            dispatch(fetchOrderInvoice({ id }));
          }
        })
        .catch((err) => toast.error(err?.message || err?.error || 'Failed to load order'));
    }
    return () => {
      dispatch(clearCurrentOrder());
    };
  }, [dispatch, id]);

  const formatDate = (s) => (s ? new Date(s).toLocaleString() : '—');
  const formatCurrency = (v, currency) => {
    const amount = Number(v);
    if (isNaN(amount)) return '—';
    const symbol = !currency || currency === 'USD' ? '$' : currency + ' ';
    return `${symbol}${amount.toFixed(2)}`;
  };

  const getStatusBadgeClass = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'processing':
      case 'confirmed':
      case 'shipped': return 'status-processing';
      case 'delivered':
      case 'completed': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-pending';
    }
  };

  const getPaymentBadgeClass = (paymentStatus) => {
    switch ((paymentStatus || '').toLowerCase()) {
      case 'completed':
      case 'paid': return 'payment-paid';
      case 'refunded': return 'payment-refunded';
      default: return 'payment-pending';
    }
  };

  const handleExportInvoice = async () => {
    if (!order) return;
    try {
          const response = await apiService.invoices.downloadPDF(currentOrderInvoice?._id);
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Invoice-${currentOrderInvoice?.invoiceNumber || currentOrderInvoice?._id}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          toast.success('PDF downloaded', { position: 'bottom-right' });
        } catch (err) {
          console.error('PDF download error:', err);
          toast.error('Failed to generate PDF. Please try again.', { position: 'bottom-right' });
        } finally {
         
        }
    // try {
    //   setIsExporting(true);
    //   const invoiceId = currentOrderInvoice?._id || order.invoiceId;
    //   if (!invoiceId) {
    //     toast.error('No invoice exists for this order yet');
    //     return;
    //   }
    //   await downloadInvoicePdf(invoiceId);
    // } catch (err) {
    //   console.error('Error exporting invoice:', err);
    //   toast.error('Failed to download PDF');
    // } finally {
    //   setIsExporting(false);
    // }
  };

  const handleGenerateInvoice = async () => {
    if (!order) return;
    try {
      await dispatch(generateOrderInvoice({ id: order._id, businessAccountId })).unwrap();
      toast.success('Invoice generated and saved successfully');
    } catch (err) {
      toast.error(err?.message || err?.error || 'Failed to generate invoice');
    }
  };

  const handleStatusChange = async ({ status, paymentStatus, notes }) => {
    if (!order) return;
    try {
      const backendStatus = STATUS_MAP[status] || status.toLowerCase();
      const backendPayment = PAYMENT_MAP[paymentStatus] || paymentStatus.toLowerCase();

      // Update order status if changed
      if (backendStatus !== order.status) {
        await dispatch(updateOrderStatus({
          id: order._id,
          status: backendStatus,
          note: notes,
          businessAccountId,
        })).unwrap();
      }

      // Update payment status if changed
      if (backendPayment !== order.paymentStatus) {
        await dispatch(updateOrderPayment({
          id: order._id,
          paymentStatus: backendPayment,
          businessAccountId,
        })).unwrap();
      }

      setIsStatusModalOpen(false);
    } catch (err) {
      toast.error(err?.message || err?.error || 'Failed to update status');
      throw err; // Let the modal's catch handle its own loading state
    }
  };

  if (detailLoading) {
    return (
      <div className="order-detail-container">
        <div className="loading-state">Loading order…</div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-detail-container">
        <div className="order-detail-header">
          <button className="btn-back" onClick={() => navigate(-1)}>← Back</button>
          <h1>Order Not Found</h1>
        </div>
        <div className="empty-state">
          {typeof error === 'string' ? error : error?.error || error?.message || 'Order could not be loaded.'}
        </div>
      </div>
    );
  }

  const displayStatus = STATUS_DISPLAY[order.status] || order.status;
  const displayPayment = PAYMENT_DISPLAY[order.paymentStatus] || order.paymentStatus;
  const subtotal = order.subtotal || 0;
  const total = order.totalAmount || 0;
  const paidAmount = order.paidAmount || 0;
  const balance = total - paidAmount;

  return (
    <div className="order-detail-container">
      <div className="order-detail-header">
        <button className="btn-back" onClick={() => navigate(-1)}>← Back</button>
        <h1>{order.orderNumber}</h1>
        <span className={`order-status-badge ${getStatusBadgeClass(order.status)}`}>{displayStatus}</span>
      </div>

      <div className="order-detail-grid">
        {/* Customer Info Card */}
        <div className="detail-card">
          <h3>Customer Information</h3>
          <div className="info-group">
            {(order.customerName || order.customerPhone) && (
              <div className="info-row">
                <span className="label">Name</span>
                <span className="value">{order.customerName || order.customerPhone}</span>
              </div>
            )}
            {order.customerPhone && order.customerName && (
              <div className="info-row">
                <span className="label">Phone</span>
                <span className="value">{order.customerPhone}</span>
              </div>
            )}
            {order.deliveryAddress && (
              <div className="info-row">
                <span className="label">Address</span>
                <span className="value">{order.deliveryAddress}</span>
              </div>
            )}
            {order.catalogId && (
              <div className="info-row">
                <span className="label">Catalog</span>
                <span className="value">{order.catalogId}</span>
              </div>
            )}
          </div>
        </div>

        {/* Order Status Card */}
        <div className="detail-card">
          <h3>Order Status</h3>
          <div className="status-info">
            <div className="status-item">
              <span className="label">Order Status</span>
              <div className="status-update-row">
                <select
                  className="status-select"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  disabled={isUpdatingStatus}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button
                  className="btn-update-status"
                  onClick={handleUpdateOrderStatus}
                  disabled={isUpdatingStatus || selectedStatus === order.status}
                >
                  {isUpdatingStatus ? '...' : 'Update'}
                </button>
              </div>
            </div>
            <div className="status-item">
              <span className="label">Payment Status</span>
              <div className="status-update-row">
                <select
                  className="status-select payment-select"
                  value={selectedPayment}
                  onChange={(e) => setSelectedPayment(e.target.value)}
                  disabled={isUpdatingPayment}
                >
                  <option value="pending">Pending</option>
                  <option value="completed">Paid</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
                <button
                  className="btn-update-payment"
                  onClick={handleUpdatePaymentStatus}
                  disabled={isUpdatingPayment || selectedPayment === order.paymentStatus}
                >
                  {isUpdatingPayment ? '...' : 'Update'}
                </button>
              </div>
            </div>
            <div className="status-item">
              <span className="label">Created</span>
              <span className="value">{formatDate(order.createdAt)}</span>
            </div>
            {order.currency && (
              <div className="status-item">
                <span className="label">Currency</span>
                <span className="value">{order.currency}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="detail-card full-width">
        <h3>Order Items</h3>
        <table className="items-table">
          <thead>
            <tr>
              <th>Description</th>
              <th className="qty-col">Qty</th>
              <th className="price-col">Rate</th>
              <th className="amount-col">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(order.productItems || []).map((item, idx) => (
              <tr key={idx}>
                <td>
                  {item.productImage && (
                    <img
                      src={item.productImage}
                      alt={item.productName}
                      style={{ width: 32, height: 32, objectFit: 'cover', marginRight: 8, verticalAlign: 'middle', borderRadius: 4 }}
                    />
                  )}
                  {item.productName || item.productRetailerId || '—'}
                  {item.productRetailerId && item.productName && (
                    <small style={{ color: '#888', marginLeft: 6 }}>({item.productRetailerId})</small>
                  )}
                </td>
                <td className="qty-col">{item.quantity}</td>
                <td className="price-col">{formatCurrency(item.itemPrice, item.currency || order.currency)}</td>
                <td className="amount-col">{formatCurrency((item.itemPrice || 0) * (item.quantity || 1), item.currency || order.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payment Summary */}
      <div className="detail-card full-width">
        <h3>Payment Summary</h3>
        <div className="summary-rows">
          <div className="summary-row">
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal, order.currency)}</span>
          </div>
          {(order.discount || 0) > 0 && (
            <div className="summary-row">
              <span>Discount</span>
              <span>-{formatCurrency(order.discount, order.currency)}</span>
            </div>
          )}
          <div className="summary-divider"></div>
          <div className="summary-row total">
            <span>Total</span>
            <span>{formatCurrency(total, order.currency)}</span>
          </div>
          {paidAmount > 0 && (
            <>
              <div className="summary-row">
                <span>Amount Paid</span>
                <span className="paid">{formatCurrency(paidAmount, order.currency)}</span>
              </div>
              <div className="summary-row">
                <span>Balance Due</span>
                <span className={balance > 0 ? 'pending' : 'paid'}>{formatCurrency(balance, order.currency)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Invoice Section */}
      <div className="detail-card full-width invoice-section">
        <h3>Invoice</h3>
        {invoiceLoading ? (
          <div className="invoice-loading">Loading invoice…</div>
        ) : currentOrderInvoice ? (
          <div className="invoice-linked">
            <div className="invoice-info">
              <div className="invoice-number">
                <span className="invoice-icon">🧾</span>
                <strong>{currentOrderInvoice.invoiceNumber}</strong>
              </div>
              <div className="invoice-meta">
                <span className={`invoice-status-badge invoice-status-${currentOrderInvoice.status}`}>
                  {currentOrderInvoice.status?.charAt(0).toUpperCase() + currentOrderInvoice.status?.slice(1)}
                </span>
                <span className="invoice-date">
                  Created {new Date(currentOrderInvoice.invoiceDate || currentOrderInvoice.createdAt).toLocaleDateString()}
                </span>
                <span className="invoice-total">
                  Total: {formatCurrency(currentOrderInvoice.total, currentOrderInvoice.currency || order.currency)}
                </span>
              </div>
            </div>
            <div className="invoice-actions">
              <button
                className="btn-invoice-view"
                onClick={() => navigate('/invoices/' + currentOrderInvoice?._id)}
                title="Go to Invoices page"
              >
                📋 View in Invoices
              </button>
              <button
                className="btn-invoice-download"
                onClick={handleExportInvoice}
                disabled={isExporting}
                title="Download invoice as PDF"
              >
                {isExporting ? '⏳ Generating…' : '⬇️ Download PDF'}
              </button>
            </div>
          </div>
        ) : (
          <div className="invoice-empty">
            <p>No invoice has been generated for this order yet.</p>
            <button
              className="btn-generate-invoice"
              onClick={handleGenerateInvoice}
              disabled={invoiceLoading}
            >
              {invoiceLoading ? '⏳ Generating…' : '🧾 Generate Invoice'}
            </button>
          </div>
        )}
      </div>

      {/* Order Text / Notes */}
      {order.orderText && (
        <div className="detail-card full-width">
          <h3>Notes</h3>
          <p className="notes-text">{order.orderText}</p>
        </div>
      )}

      {/* Tracking Info */}
      {order.trackingNumber && (
        <div className="detail-card full-width">
          <h3>Shipping</h3>
          <div className="info-group">
            <div className="info-row">
              <span className="label">Tracking #</span>
              <span className="value">{order.trackingNumber}</span>
            </div>
            {order.carrier && (
              <div className="info-row">
                <span className="label">Carrier</span>
                <span className="value">{order.carrier}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="action-buttons">
        <button
          className="btn-primary"
          onClick={handleExportInvoice}
          disabled={isExporting}
        >
          {isExporting ? '⏳ Exporting...' : '📄 Download Invoice PDF'}
        </button>
        <button className="btn-secondary" onClick={() => setIsStatusModalOpen(true)}>
          🔄 Change Status
        </button>
      </div>

      {/* Status Change Modal */}
      <StatusChangeModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onConfirm={handleStatusChange}
        currentStatus={displayStatus}
        currentPaymentStatus={displayPayment}
        orderId={order.orderNumber}
      />
    </div>
  );
};

export default OrderDetail;

